import { Inject, Injectable } from '../../injection';
import { getNumericEnumKeyName } from '../../utils';
import { ILogContext } from './log-context.interface';
import { ILogHandler, ILogHandlerStatus } from './log-handler.interface';
import { LogLevel } from './log-level.enum';
import { IWebConsoleLogHandlerConfiguration } from './web-console-log-handler-configuration.interface';

/**
 * A simple Web Console log handler that is used as fallback when no other handlers are available.
 *
 * NOTE: This service is instantiated directly by the LoggerService and is not expose to the Angular DI.
 */
@Injectable()
export class WebConsoleLogHandlerService implements ILogHandler {
  public readonly enabled: boolean;
  public readonly name: string = 'Web Console';

  constructor(
    @Inject(IWebConsoleLogHandlerConfiguration)
    readonly configuration: IWebConsoleLogHandlerConfiguration,
  ) {
    this.enabled = configuration.enabled;
  }

  public async getStatus(): Promise<ILogHandlerStatus> {
    return Promise.resolve({
      handlerName: this.name,
      enabled: this.enabled,
    });
  }

  /**
   * Logs a message with an arbitrary level to the Web Console.
   */
  public async handle(level: LogLevel, message: string, context?: ILogContext): Promise<null> {
    if (!this.enabled) {
      return Promise.resolve(null);
    }
    const logger: Console = window.console;
    const messageLevel: string = getNumericEnumKeyName(LogLevel, level);

    switch (level) {
      case LogLevel.EMERGENCY:
      case LogLevel.ALERT:
      case LogLevel.CRITICAL:
      case LogLevel.ERROR:
        logger.error(`${messageLevel}: ${message}`);
        logger.error(`${messageLevel} CONTEXT: `, context);
        break;

      case LogLevel.WARNING:
        logger.warn(`${messageLevel}: ${message}`);
        logger.warn(`${messageLevel} CONTEXT: `, context);
        break;

      case LogLevel.NOTICE:
      case LogLevel.INFO:
      case LogLevel.DEBUG:
        logger.log(`${messageLevel}: ${message}`);
        logger.log(`${messageLevel} CONTEXT: `, context);
        break;
    }

    return Promise.resolve(null);
  }
}
